/**
 * RebookWorkshopPicker — the "Choose a workshop" browse step of the rebook
 * drawer (BON-146).
 *
 * The rebook-side caller of the shared {@link WorkshopPicker}: it composes the
 * filter row (region as a label, not a control) and the header blurb naming the
 * workshop being left, and hands over rows the drawer already fetched. The
 * shell, the calendar, the day drawer and the selection behaviour are shared
 * verbatim with creating a booking — there is no second picker.
 *
 * Ineligible workshops arrive from the server already marked unselectable with
 * a reason, so a full or off-price date the CSR is looking for renders greyed
 * out with the reason rather than silently missing.
 */
import type { PickerViewMode } from './pickerViewMode';
import type { PickerWorkshop } from './pickerWorkshop';
import { RebookPickerFilters } from './RebookPickerFilters';
import { RebookTruncatedNotice } from './RebookTruncatedNotice';
import { WorkshopPicker } from './WorkshopPicker';
import type { FilterOption } from './WorkshopPickerHelpers';

export interface RebookWorkshopPickerProps {
  workshops: PickerWorkshop[];
  loading: boolean;
  /** Message from a failed targets load, or null. */
  error: string | null;
  /** The server hit its row cap, so this month is not fully represented. */
  truncated: boolean;
  /** The booking's region — rendered as a fixed label. */
  regionName: string | null;
  /** What the customer is being moved off, e.g. "Dwarf Jade · Loyal Legion". */
  sourceLabel: string;
  /** Replaces the default "Moving this booking from…" line. */
  headerSubtitle?: React.ReactNode;
  treeOptions: FilterOption[];
  treeSpeciesId: string | null;
  onTreeChange: (id: string | null) => void;
  viewMode: PickerViewMode;
  onViewModeChange: (mode: PickerViewMode) => void;
  month: Date;
  onMonthChange: (month: Date) => void;
  onBack: () => void;
  onClose: () => void;
  onPick: (workshop: PickerWorkshop) => void;
}

export function RebookWorkshopPicker({
  workshops,
  loading,
  error,
  truncated,
  regionName,
  sourceLabel,
  headerSubtitle,
  treeOptions,
  treeSpeciesId,
  onTreeChange,
  viewMode,
  onViewModeChange,
  month,
  onMonthChange,
  onBack,
  onClose,
  onPick,
}: RebookWorkshopPickerProps) {
  return (
    <WorkshopPicker
      workshops={workshops}
      loading={loading}
      error={error !== null}
      viewMode={viewMode}
      month={month}
      onMonthChange={onMonthChange}
      headerSubtitle={
        headerSubtitle ?? <>Moving this booking from {sourceLabel}. Same price, same region.</>
      }
      filters={
        <RebookPickerFilters
          regionName={regionName}
          treeOptions={treeOptions}
          treeSpeciesId={treeSpeciesId}
          onTreeChange={onTreeChange}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          month={month}
          onMonthChange={onMonthChange}
        />
      }
      notice={truncated ? <RebookTruncatedNotice /> : undefined}
      dayScopeLabel={regionName ?? 'this region'}
      emptyMessage="No workshops this month match. Try another month, or clear the tree filter."
      errorMessage={error ?? 'Could not load workshops.'}
      onBack={onBack}
      onClose={onClose}
      onPick={onPick}
    />
  );
}
